import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote, ChevronLeft, ChevronRight, PlaneTakeoff, Luggage, HeartHandshake, Activity, ShieldCheck, Phone } from "lucide-react";
import { TESTIMONIALS, COMPANY } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { useQuote } from "./QuoteModal";

export const SERVICE_ICONS = {
  PlaneTakeoff,
  Luggage,
  HeartHandshake,
  Activity,
  ShieldCheck,
};

export function PillBadge({ children, light = false }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] ${light ? "bg-white/15 text-[#D2EBF8] text-[#D2EBF8]" : "bg-[#E2F2FA] text-[#006FB9]"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${light ? "bg-[#B9DFF3]" : "bg-[#006FB9]"}`} />
      {children}
    </span>
  );
}

export function SectionHeading({ badge, title, subtitle, center = true, light = false }) {
  return (
    <div className={`${center ? "mx-auto text-center" : ""} max-w-2xl`} data-reveal>
      {badge && <PillBadge light={light}>{badge}</PillBadge>}
      <h2 className={`mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight sm:text-4xl lg:text-[44px] ${light ? "text-white" : "text-[#0B2748]"}`}>{title}</h2>
      {subtitle && <p className={`mt-4 text-[15.5px] leading-relaxed ${light ? "text-[#C5DEEC]" : "text-[#526A7D]"}`}>{subtitle}</p>}
    </div>
  );
}

// Inner-page hero banner
export function PageHero({ badge, title, subtitle, image }) {
  return (
    <section className="relative overflow-hidden bg-[#073B67]">
      {image && (
        <>
          <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#073B67] via-[#073B67]/80 to-[#073B67]/40" />
        </>
      )}
      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <div className="hero-stagger max-w-2xl">
          <PillBadge light>{badge}</PillBadge>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl">{title}</h1>
          {subtitle && <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[#C5DEEC]">{subtitle}</p>}
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({ service, index = 0 }) {
  const Icon = SERVICE_ICONS[service.icon] || ShieldCheck;
  return (
    <Link
      to={`/services/${service.slug}`}
      data-reveal
      style={{ transitionDelay: `${index * 80}ms` }}
      data-testid={`service-card-${service.slug}`}
      className="tilt-card group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:shadow-[0_24px_48px_rgba(0,111,185,0.16)]"
    >
      <div className="relative h-52 overflow-hidden">
        <img src={service.image} alt={service.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#073B67]/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        <span className="absolute -bottom-6 right-6 grid h-14 w-14 place-items-center rounded-2xl bg-[#006FB9] shadow-lg transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#B9DFF3]">
          <Icon className="h-6 w-6 text-[#B9DFF3] transition-colors duration-500 group-hover:text-[#006FB9]" />
        </span>
      </div>
      <div className="flex flex-1 flex-col p-7 pt-9">
        <h3 className="font-heading text-xl font-bold text-[#0B2748]">{service.title}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#526A7D]">{service.short}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
          Learn More
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const ref = useRevealGroup();

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);

  const item = TESTIMONIALS[active];

  return (
    <section ref={ref} className="bg-[#F4F7FB] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading badge="Testimonials" title="Secured Lives, Happy Clients" subtitle="Real words from the families and professionals we've protected across British Columbia." />
        <div className="mx-auto mt-14 max-w-3xl" data-reveal>
          <div key={active} className="tilt-card testimonial-swap relative rounded-3xl bg-white p-10 shadow-[0_8px_40px_rgba(0,111,185,0.09)] sm:p-12">
            <Quote className="absolute right-8 top-8 h-12 w-12 text-[#E2F2FA]" />
            <p className="text-lg leading-relaxed text-[#294A65]">“{item.text}”</p>
            <div className="mt-8 flex items-center gap-4">
              <img src={item.image} alt={item.name} className="h-14 w-14 rounded-full object-cover ring-4 ring-[#E2F2FA]" />
              <div>
                <p className="font-heading text-base font-bold text-[#0B2748]">{item.name}</p>
                <p className="text-sm text-[#526A7D]">{item.role}</p>
              </div>
            </div>
          </div>
          <div className="mt-8 flex items-center justify-center gap-4">
            <button aria-label="Previous" onClick={() => setActive((active - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="grid h-11 w-11 place-items-center rounded-full border border-[#D5E4ED] text-[#006FB9] transition-all duration-300 hover:bg-[#006FB9] hover:text-white" data-testid="testimonial-prev">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} aria-label={`Slide ${i + 1}`} className={`h-2 rounded-full transition-all duration-400 ${i === active ? "w-8 bg-[#006FB9]" : "w-2 bg-[#C7DEEB]"}`} />
              ))}
            </div>
            <button aria-label="Next" onClick={() => setActive((active + 1) % TESTIMONIALS.length)} className="grid h-11 w-11 place-items-center rounded-full border border-[#D5E4ED] text-[#006FB9] transition-all duration-300 hover:bg-[#006FB9] hover:text-white" data-testid="testimonial-next">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CTASection({ title = "Secure What Matters Most!", subtitle }) {
  const { openQuote } = useQuote();
  const ref = useRevealGroup();
  return (
    <section ref={ref} className="relative overflow-hidden bg-[#006FB9]">
      <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#B9DFF3]/10 blur-2xl" />
      <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#B9DFF3]/10 blur-2xl" />
      <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-8 px-6 py-16 text-center lg:flex-row lg:justify-between lg:py-20 lg:text-left">
        <div data-reveal>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{title}</h2>
          <p className="mt-3 max-w-xl text-[15.5px] leading-relaxed text-[#C5DEEC]">
            {subtitle || "Free consultations 7 days a week. No hidden fees. Your satisfaction is 100% guaranteed."}
          </p>
        </div>
        <div className="flex flex-col items-center gap-4 sm:flex-row" data-reveal>
          <button onClick={() => openQuote()} className="btn-light" data-testid="cta-get-quote">
            Get a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a href={COMPANY.phoneHref} className="btn-ghost-light" data-testid="cta-call">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
        </div>
      </div>
    </section>
  );
}
